/**
 * Audit Trail Collector
 *
 * Collects complete audit trail of all metadata interpreter operations.
 * Life-critical: Immutable log of every field change, transformation, validation.
 *
 * All operations logged with:
 * - What changed
 * - Who changed it
 * - When it changed
 * - Original and new values
 * - Which primitive(s) affected
 * - Compliance flags
 */

/**
 * Audit entry
 */
export interface AuditEntry {
  // Identity
  id: string; // Snowflake ID or timestamp-based
  timestamp: string; // ISO 8601
  sequence: number; // Sequential number within session

  // Action
  action: AuditAction;
  actor: string; // Who performed action

  // Details
  details: AuditDetails;

  // Compliance
  compliance_flags?: string[]; // HIPAA, SOX, etc.
  severity: 'info' | 'warning' | 'error' | 'critical';

  // Context
  template_id: string;
  template_version: string;
  session_id?: string;
  correlation_id?: string;

  // Preserve for future
  metadata_version?: string;
  interpreter_version?: string;
  browser_info?: string;
  ip_address?: string;
}

export type AuditAction =
  | 'metadata_loaded'
  | 'metadata_validated'
  | 'primitives_initialized'
  | 'form_field_changed'
  | 'transformation_applied'
  | 'conditional_evaluated'
  | 'primitive_updated'
  | 'form_submitted'
  | 'validation_error'
  | 'error_occurred';

export interface AuditDetails {
  // Field changes
  field_id?: string;
  field_label?: string;
  old_value?: any;
  new_value?: any;

  // Primitive updates
  primitive_id?: string;
  primitive_type?: string;
  primitive_field_path?: string;

  // Transformations
  transformation_type?: string;
  transformation_spec?: any;

  // Errors
  error_code?: string;
  error_message?: string;
  error_stack?: string;

  // Preserve all context
  [key: string]: any;
}

/**
 * Audit Trail Collector
 */
export class AuditTrailCollector {
  private entries: AuditEntry[];
  private sequence: number;
  private session_id: string;

  constructor(session_id?: string) {
    this.entries = [];
    this.sequence = 0;
    this.session_id =
      session_id || `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Record audit entry
   * Life-critical: Never fails, always logs
   */
  record(
    action: AuditAction,
    details: AuditDetails,
    template_id: string,
    template_version: string,
    actor: string,
    compliance_flags?: string[],
  ): void {
    try {
      const entry: AuditEntry = {
        id: `audit_${Date.now()}_${this.sequence}`,
        timestamp: new Date().toISOString(),
        sequence: this.sequence++,
        action,
        actor,
        details,
        compliance_flags,
        severity: this.determineSeverity(action, details),
        template_id,
        template_version,
        session_id: this.session_id,
      };

      this.entries.push(entry);

      // Also log to console for development
      console.log('[AuditTrail]', action, details);
    } catch (error) {
      // Audit logging must never fail
      console.error('[AuditTrail] Failed to record entry (non-fatal):', error);
    }
  }

  /**
   * Determine severity based on action and details
   */
  private determineSeverity(
    action: AuditAction,
    details: AuditDetails,
  ): 'info' | 'warning' | 'error' | 'critical' {
    if (action === 'error_occurred') return 'error';
    if (action === 'validation_error') return 'warning';
    if (details.compliance_flags && details.compliance_flags.includes('HIPAA')) return 'critical';
    return 'info';
  }

  /**
   * Get all entries
   */
  getEntries(): AuditEntry[] {
    return [...this.entries];
  }

  /**
   * Get entries by action
   */
  getEntriesByAction(action: AuditAction): AuditEntry[] {
    return this.entries.filter((e) => e.action === action);
  }

  /**
   * Export audit trail
   * For compliance reporting
   */
  export(): string {
    return JSON.stringify(
      {
        session_id: this.session_id,
        total_entries: this.entries.length,
        entries: this.entries,
        exported_at: new Date().toISOString(),
      },
      null,
      2,
    );
  }

  /**
   * Clear audit trail
   * For new sessions
   */
  clear(): void {
    this.entries = [];
    this.sequence = 0;
  }
}
